/**
 * phanHeRoles — quyền & flag theo phân hệ (BSC / VH / QT).
 *
 * Logic:
 * - profile giữ đủ listRole 3 phân hệ + 6 flag.
 * - isAdmin / isQuanLy được xác định theo phân hệ hiện tại đang mở.
 *
 * Các hàm:
 * - filterListRoleByPhanHe: Lọc listRole theo siteModule/phanHe (nếu legacy không có tag thì giữ lại).
 * - getPhanHeFlags: Trả về isAdmin/isQuanLytheo PH.
 * - applyPhanHeSliceToProfile: Gán isAdmin/isQuanLy + chuẩn hóa availablePhanHe.
 * - getAvailablePhanHeList / hasAvailablePhanHe: Dùng cho Intro/Navbar giao với license.
 */
import type { ProfileRoleItem, ProfileUser } from '@/types/authen';
import { CONSTANTS } from '@/utils/constants';
import { convertPhanHeToNumber } from '@/utils/phanHeStorage';

export type PhanHeKey = 'bsc' | 'vanhoa' | 'quytrinh';

export type PhanHeFlags = {
    isAdmin: boolean;
    isQuanLy: boolean;
};

function normalizePhanHe(phanHe?: string | null): PhanHeKey {
    const value = String(phanHe || '').trim().toLowerCase();
    if (value === CONSTANTS.PHAN_HE.VAN_HOA) return 'vanhoa';
    if (value === CONSTANTS.PHAN_HE.QUY_TRINH) return 'quytrinh';
    return 'bsc';
}

/** Lọc listRole theo phân hệ đang mở (siteModule 0/1/2). */
export function filterListRoleByPhanHe(
    listRole: ProfileRoleItem[] | unknown[] | null | undefined,
    phanHe?: string | null,
): ProfileRoleItem[] {
    if (!Array.isArray(listRole) || listRole.length === 0) return [];

    const siteModule = convertPhanHeToNumber(normalizePhanHe(phanHe));
    const phanHeKey = normalizePhanHe(phanHe);

    return (listRole as ProfileRoleItem[]).filter((role) => {
        if (role == null || typeof role !== 'object') return false;
        const roleModule = role.siteModule ?? (role as { SiteModule?: number }).SiteModule;
        if (typeof roleModule === 'number') return roleModule === siteModule;

        const rolePhanHe = String(
            role.phanHe ?? (role as { PhanHe?: string }).PhanHe ?? '',
        )
            .trim()
            .toLowerCase();
        if (rolePhanHe) return rolePhanHe === phanHeKey;

        // Legacy role không có tag: giữ lại (backward compat trước khi BE deploy)
        return true;
    });
}

/** Lấy isAdmin / isQuanLy theo phân hệ từ 6 flag profile. */
export function getPhanHeFlags(
    profile: ProfileUser | null | undefined,
    phanHe?: string | null,
): PhanHeFlags {
    if (!profile) return { isAdmin: false, isQuanLy: false };

    const key = normalizePhanHe(phanHe);
    if (key === 'vanhoa') {
        return {
            isAdmin: Boolean(profile.isAdminVanHoa),
            isQuanLy: Boolean(profile.isQuanLyVanHoa),
        };
    }
    if (key === 'quytrinh') {
        return {
            isAdmin: Boolean(profile.isAdminQuyTrinh),
            isQuanLy: Boolean(profile.isQuanLyQuyTrinh),
        };
    }
    return {
        isAdmin: Boolean(profile.isAdminBsc ?? profile.isAdmin),
        isQuanLy: Boolean(profile.isQuanLyBsc ?? profile.isQuanLy),
    };
}

/** Apply lát isAdmin/isQuanLy đang mở lên profile (giữ full listRole + 6 flag). */
export function applyPhanHeSliceToProfile(
    profile: ProfileUser,
    phanHe?: string | null,
): ProfileUser {
    const flags = getPhanHeFlags(profile, phanHe);
    const availablePhanHe = getAvailablePhanHeList(profile);
    return {
        ...profile,
        availablePhanHe,
        isAdmin: flags.isAdmin,
        isQuanLy: flags.isQuanLy,
    };
}

/** Parse availablePhanHe từ profile (array hoặc suy ra từ 6 flag / listRole). */
export function getAvailablePhanHeList(
    profile: ProfileUser | null | undefined,
    fallback?: unknown,
): string[] {
    if (!profile) return [];
    const raw = profile.availablePhanHe;
    if (Array.isArray(raw)) {
        return [...new Set(raw.map((item) => String(item).trim().toLowerCase()).filter(Boolean))];
    }

    const inferred = new Set<string>();
    if (profile.isAdminBsc || profile.isQuanLyBsc) inferred.add('bsc');
    if (profile.isAdminVanHoa || profile.isQuanLyVanHoa) inferred.add('vanhoa');
    if (profile.isAdminQuyTrinh || profile.isQuanLyQuyTrinh) inferred.add('quytrinh');

    if (Array.isArray(profile.listRole)) {
        for (const role of profile.listRole) {
            // eslint-disable-next-line @next/next/no-assign-module-variable
            const module = role?.siteModule ?? (role as { SiteModule?: number })?.SiteModule;
            if (module === 0) inferred.add('bsc');
            else if (module === 1) inferred.add('vanhoa');
            else if (module === 2) inferred.add('quytrinh');
            const ph = String(role?.phanHe ?? (role as { PhanHe?: string })?.PhanHe ?? '')
                .trim()
                .toLowerCase();
            if (ph === 'bsc' || ph === 'vanhoa' || ph === 'quytrinh') inferred.add(ph);
        }
    }

    if (inferred.size === 0) {
        const fallbackItems = Array.isArray(fallback)
            ? fallback
            : String(fallback ?? '').split(',');
        for (const item of fallbackItems) {
            const value = String(item).trim().toLowerCase();
            if (value) inferred.add(value);
        }
    }

    // Legacy profile: chưa có tag — fallback BSC để không mất quyền
    if (inferred.size === 0 && (profile.isAdmin || profile.isQuanLy || (profile.listRole?.length ?? 0) > 0)) {
        inferred.add('bsc');
    }

    return [...inferred];
}

export function hasAvailablePhanHe(
    profile: ProfileUser | null | undefined,
    key: string,
): boolean {
    return getAvailablePhanHeList(profile).includes(String(key).trim().toLowerCase());
}
